import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Lock, Mail } from 'lucide-react';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Alert from '../../components/ui/Alert';

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { login, isLoading } = useAuth();
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [error, setError] = useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.email || !formData.password) {
      setError('Please enter both username/email and password');
      return;
    }

    try {
      await login(formData.email, formData.password);
      navigate('/dashboard');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-bg-primary flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo and branding */}
        <div className="text-center mb-8">
          <div className="mb-4 flex justify-center">
            <img 
              src="/login-logo.png" 
              alt="SecureView Logo" 
              className="h-24 w-auto object-contain"
            />
          </div>
          <h1 className="text-3xl font-bold text-text-primary mb-2">SecureView</h1>
          <p className="text-text-muted">Smart Security Solutions</p>
        </div>

        {/* Login card */}
        <div className="bg-surface-primary rounded-card shadow-card border border-surface-secondary p-8">
          <h2 className="text-2xl font-semibold text-text-primary mb-6 text-center">
            Sign In
          </h2>

          {error && (
            <div className="mb-6">
              <Alert variant="error" message={error} onClose={() => setError(null)} />
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              name="email"
              type="text"
              label="Username or Email"
              placeholder="username or user@example.com"
              value={formData.email}
              onChange={handleChange}
              leftIcon={<Mail size={18} />}
              fullWidth
              autoComplete="username"
              disabled={isLoading}
            />

            <div>
              <Input
                name="password"
                type="password"
                label="Password"
                placeholder="Enter your password"
                value={formData.password}
                onChange={handleChange}
                leftIcon={<Lock size={18} />}
                fullWidth
                autoComplete="current-password"
                disabled={isLoading}
              />
              <div className="text-right mt-2">
                <button
                  type="button"
                  onClick={() => navigate('/forgot-password')}
                  className="text-sm text-accent-primary hover:text-accent-secondary transition-colors font-medium"
                >
                  Forgot Password?
                </button>
              </div>
            </div>

            <Button
              type="submit"
              variant="primary"
              size="lg"
              fullWidth
              isLoading={isLoading}
            >
              {isLoading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>

          <div className="mt-6 text-center text-sm text-text-muted">
            Don't have an account?{' '}
            <Link 
              to="/signup" 
              className="text-accent-primary hover:text-accent-secondary transition-colors font-medium"
            >
              Sign Up
            </Link>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-6 text-sm text-text-muted">
          © 2026 SecureView. All rights reserved.
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
